import * as Sentry from "@sentry/browser";
import { LogLayer, LogLevel } from "loglayer";
import { SentryTransport } from "@loglayer/transport-sentry";

import { ConsoleBadgeTransport, prefixContextPlugin, RecentLogsTransport } from "@archipad-js/core/logging";

// Declares the LogLayer context / metadata attributes (module augmentation)
import "@core/services/logging/logAttributes";

/**
 * Main page vs. Node tools and service worker (no `document`): only the page prints every level in the console, the others
 * only the warnings / errors. Sentry is not concerned: its transport is always added, but `Sentry.logger` does nothing until
 * `Sentry.init()`, which only the page calls.
 */
const isBrowserPage = typeof document !== "undefined";

/** The last logs of every level, included in the support diagnostic */
export const recentLogs = new RecentLogsTransport({ level: LogLevel.debug, capacity: 1000 });

/**
 * Root logger of the structured logs ([LogLayer](https://loglayer.dev)), the domain loggers are its children
 * (`log.withPrefix("[file]")`, see also `@archipad/logs/logger`); attributes are declared in `@core/services/logging/logAttributes`:
 * - printed in the console, the prefix as a badge (`debug` logs are only visible with the "Verbose" console level)
 * - sent to Sentry Logs from `info`, when Sentry is enabled (`Sentry.logger` does nothing until `Sentry.init()`)
 * - kept in memory for the support diagnostic (`recentLogs`)
 *
 * Context and metadata are flattened alongside the message, so every attribute can be filtered in Sentry.
 * Use a child logger to bind attributes without altering the shared loggers:
 * ```ts
 * fileLog.child().withContext({ "file.path": path }).withMetadata({ "file.size": size }).info("file.upload");
 * ```
 *
 * The transports are set here, at import: `child()` / `withPrefix()` copy the transports of their parent when created.
 * Plugins can be added later (`log.addPlugins()`), they are shared with the children.
 */
export const log = new LogLayer({
    transport: [
        new ConsoleBadgeTransport({ level: isBrowserPage ? LogLevel.debug : LogLevel.warn }),
        new SentryTransport({ logger: Sentry.logger, level: LogLevel.info }),
        recentLogs,
    ],
    plugins: [prefixContextPlugin()],
    errorFieldName: "error",
    // Sentry attributes must be primitives
    errorSerializer: (error) => (error instanceof Error ? `${error.name}: ${error.message}` : String(error)),
});
